// Profile tab editing (B6) — right-side drawer editing ONE carried intake
// section. FIND-12 discipline: every field hydrates from the current
// profile and the save sends the FULL profile (all six sections) with only
// this section's values replaced, so open-edit-save-unchanged is a
// zero-change write server-side. The Deal snapshot is never modified —
// edits store loan-local values and flip the field's provenance caption
// to "Edited post-close".

import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useUpdateLoanIntakeProfile } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type {
  LoanIntakeProfileDto,
  UpdateLoanIntakeProfileRequest,
} from "@/types/intakeProfile";
import { ResourceDrawer } from "../common/ResourceDrawer";
import {
  INTAKE_SECTIONS,
  sectionValues,
  type IntakeFieldConfig,
  type IntakeFieldValue,
  type IntakeSectionId,
} from "./intakeSectionConfig";

interface IntakeSectionEditDrawerProps {
  loanId: string;
  profile: LoanIntakeProfileDto;
  sectionId: IntakeSectionId;
  open: boolean;
  onClose: () => void;
}

/** Field value → input string (hydration). */
function toInput(value: IntakeFieldValue): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

/** Input string → field value (save). Throws a user-facing message on a
 *  non-numeric money value. */
function fromInput(field: IntakeFieldConfig, raw: string): IntakeFieldValue {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  switch (field.type) {
    case "money": {
      const n = Number(trimmed.replace(/[$,]/g, ""));
      if (!Number.isFinite(n)) throw new Error(`${field.label} must be a number.`);
      return n;
    }
    case "yesno":
      return trimmed === "true";
    default:
      return trimmed;
  }
}

export function IntakeSectionEditDrawer({
  loanId,
  profile,
  sectionId,
  open,
  onClose,
}: IntakeSectionEditDrawerProps) {
  const section = INTAKE_SECTIONS.find((s) => s.id === sectionId)!;
  const toast = useToast();
  const update = useUpdateLoanIntakeProfile(loanId);
  const [error, setError] = useState<string | null>(null);

  // Hydrate EVERY field of the section from the current profile (FIND-12).
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      section.fields.map((f) => [
        f.key,
        toInput(sectionValues(profile, sectionId)[f.key]),
      ]),
    ),
  );

  const save = async () => {
    setError(null);
    let edited: Record<string, IntakeFieldValue>;
    try {
      edited = {
        ...sectionValues(profile, sectionId),
        ...Object.fromEntries(
          section.fields.map((f) => [f.key, fromInput(f, draft[f.key] ?? "")]),
        ),
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }

    // Full-replace payload: the other five sections pass through untouched.
    const body = {
      structure: profile.structure,
      seller: profile.seller,
      buyer: profile.buyer,
      transaction: profile.transaction,
      motivation: profile.motivation,
      broker: profile.broker,
      [sectionId]: edited,
    } as unknown as UpdateLoanIntakeProfileRequest;

    try {
      await update.mutateAsync(body);
      toast.success("Section saved");
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const inputClass =
    "mt-1 w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure";

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={`Edit ${section.title}`}
      subtitle="Stored on the loan record — the originating Deal is never modified."
      testId="intake-section-edit-drawer"
      footer={
        <>
          {error && (
            <p
              className="text-caption text-status-error m-0 mr-auto"
              role="alert"
              data-testid="intake-edit-error"
            >
              {error}
            </p>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void save()}
            loading={update.isPending}
            data-testid="intake-edit-save"
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        {section.fields.map((f) => (
          <div key={f.key}>
            <label
              htmlFor={`intake-edit-${f.key}`}
              className="font-display text-caption uppercase tracking-label text-gray-mid"
            >
              {f.label}
            </label>
            {f.type === "textarea" ? (
              <textarea
                id={`intake-edit-${f.key}`}
                className={`${inputClass} min-h-[88px]`}
                value={draft[f.key] ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                }
                data-testid={`intake-edit-${f.key}`}
              />
            ) : f.type === "select" || f.type === "yesno" ? (
              <select
                id={`intake-edit-${f.key}`}
                className={inputClass}
                value={draft[f.key] ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                }
                data-testid={`intake-edit-${f.key}`}
              >
                <option value="">—</option>
                {(f.type === "yesno"
                  ? [
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]
                  : f.options ?? []
                ).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`intake-edit-${f.key}`}
                type="text"
                inputMode={f.type === "money" ? "decimal" : undefined}
                className={inputClass}
                value={draft[f.key] ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                }
                data-testid={`intake-edit-${f.key}`}
              />
            )}
          </div>
        ))}
      </div>
    </ResourceDrawer>
  );
}
